import { useAuth } from '@clerk/clerk-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Pill } from '@/components/ui/Pill'
import { ApiError } from '@/lib/api/client'
import { listAdminReports, resolveAdminReport } from '@/lib/api/admin'
import { cn } from '@/lib/cn'
import type { AdminReport, ResolveReportAction } from '@/lib/types/admin'
import type { ListingStatus } from '@/lib/types/listing'

const STATUS_LABEL: Record<ListingStatus, string> = {
  pending: 'Pending',
  live: 'Live',
  quarantined_by_system: 'Under review',
  removed_by_user: 'Removed',
  taken_down_by_admin: 'Taken down',
}

function formatWhen(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function preview(reason: string): string {
  const line = reason.trim().split(/\n/)[0] ?? ''
  return line.length > 72 ? `${line.slice(0, 72)}…` : line
}

function actionError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return "You don't have access."
    if (err.status === 409) return 'Already resolved.'
    return err.detail
  }
  return "Couldn't update that report."
}

export function AdminReports() {
  const { getToken } = useAuth()
  const [reports, setReports] = useState<AdminReport[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [mobileDetail, setMobileDetail] = useState(false)

  const selected = reports.find((item) => item.id === selectedId) ?? null

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const next = await listAdminReports(getToken)
      setReports(next)
      setSelectedId((current) => {
        if (current && next.some((item) => item.id === current)) {
          return current
        }
        return next[0]?.id ?? null
      })
    } catch (err) {
      setError(
        err instanceof ApiError ? err.detail : "Couldn't load reports.",
      )
      setReports([])
      setSelectedId(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [getToken])

  async function resolve(report: AdminReport, action: ResolveReportAction) {
    if (
      action === 'uphold' &&
      !window.confirm(`Take ${report.listingBrand} down?`)
    ) {
      return
    }

    setBusy(true)
    setError(null)
    try {
      await resolveAdminReport(report.id, action, getToken)
      const next = await listAdminReports(getToken)
      setReports(next)
      setSelectedId(next[0]?.id ?? null)
      setMobileDetail(false)
    } catch (err) {
      setError(actionError(err))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <p className="font-body text-body-md text-fg-muted">Loading…</p>
  }

  if (reports.length === 0 && !error) {
    return (
      <p className="font-body text-body-md text-fg-muted">No open reports.</p>
    )
  }

  return (
    <div className="space-y-4">
      {error && (
        <p className="font-body text-body-sm text-error" role="alert">
          {error}
        </p>
      )}
      <div className="border-glass-border grid min-h-[28rem] overflow-hidden rounded-lg border md:grid-cols-[minmax(16rem,20rem)_1fr]">
        <ul
          className={cn(
            'border-glass-border divide-y overflow-y-auto md:border-r',
            mobileDetail && 'hidden md:block',
          )}
        >
          {reports.map((report) => {
            const active = report.id === selectedId
            return (
              <li key={report.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(report.id)
                    setMobileDetail(true)
                  }}
                  className={cn(
                    'w-full px-4 py-3 text-left transition-colors',
                    active
                      ? 'bg-primary-container/15'
                      : 'hover:bg-white/5',
                  )}
                >
                  <p className="font-display text-fg truncate font-semibold">
                    {report.listingBrand}
                  </p>
                  <p className="font-body text-body-sm text-fg-muted truncate">
                    {preview(report.reason)}
                  </p>
                  <p className="font-body text-label-sm text-fg-muted mt-1">
                    {formatWhen(report.createdAt)}
                  </p>
                </button>
              </li>
            )
          })}
        </ul>

        <section
          className={cn(
            'flex flex-col gap-5 p-5',
            !mobileDetail && 'hidden md:flex',
          )}
        >
          {selected ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setMobileDetail(false)
                }}
                className="font-body text-label-sm text-primary hover:text-primary-fixed-dim self-start md:hidden"
              >
                Back to list
              </button>

              <div className="space-y-2">
                <h2 className="font-display text-headline-md text-fg">
                  {selected.listingBrand}
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="neutral">{selected.listingType}</Pill>
                  <Pill tone="neutral">
                    {STATUS_LABEL[selected.listingStatus]}
                  </Pill>
                </div>
                <p className="font-body text-label-sm text-fg-muted">
                  {formatWhen(selected.createdAt)}
                </p>
              </div>

              <p className="font-body text-body-md text-fg text-pretty">
                {selected.reason}
              </p>

              <p className="font-body text-body-sm text-fg-muted break-all">
                <span className="text-label-caps uppercase">URL · </span>
                <a
                  href={selected.destinationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-fixed-dim underline underline-offset-2"
                >
                  {selected.destinationUrl}
                </a>
              </p>

              <p className="font-body text-label-sm text-fg-muted">
                Posted by @{selected.posterUsername} · Reported by @
                {selected.reporterUsername}
              </p>

              <div className="mt-auto flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  disabled={busy}
                  onClick={() => {
                    void resolve(selected, 'uphold')
                  }}
                >
                  {busy ? 'Working…' : 'Uphold'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  disabled={busy}
                  onClick={() => {
                    void resolve(selected, 'dismiss')
                  }}
                >
                  Dismiss
                </Button>
              </div>
            </>
          ) : (
            <p className="font-body text-body-md text-fg-muted">
              Select a report.
            </p>
          )}
        </section>
      </div>
    </div>
  )
}
